import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@sunfish/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        // Status-style variants: tinted background + colored border +
        // colored text. The colored border keeps the pill visibly distinct
        // even at low background opacity (e.g. amber on dark navy). For a
        // loud filled treatment override at the call-site with
        // `bg-positive text-positive-foreground`.
        destructive:
          "border-destructive/40 bg-destructive/15 text-destructive hover:bg-destructive/25",
        positive:
          "border-positive/40 bg-positive/15 text-positive hover:bg-positive/25",
        warning:
          "border-warning/50 bg-warning/15 text-warning hover:bg-warning/25",
        info:
          "border-info/40 bg-info/15 text-info hover:bg-info/25",
        outline: "text-foreground",
        // Subtle muted variant for neutral status pills (e.g. "Cancelled",
        // "Archived") that should read as quiet without taking on a strong
        // semantic color.
        muted:
          "border-transparent bg-muted text-muted-foreground hover:bg-muted/80",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
